import React from 'react';
import { HelpCircle } from 'lucide-react';
import { CreatePropertyDto } from '../../../handling_data/dtos/PropertyDto';

interface ConfirmationStepProps {
    t: any;
    formData: CreatePropertyDto;
    mainImageFile: File | null;
    galleryFiles: File[];
    existingImages: { id: number, url: string, is_main: boolean }[];
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
    t,
    formData,
    mainImageFile,
    galleryFiles,
    existingImages
}) => {
    const mainPreview = mainImageFile ? URL.createObjectURL(mainImageFile) : existingImages.find(img => img.is_main)?.url;
    const galleryCount = galleryFiles.length + existingImages.filter(img => !img.is_main).length;

    return (
        <div className="space-y-6 animate-fade-in text-center py-10">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <HelpCircle className="w-10 h-10 text-primary animate-bounce" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800">
                {t('property_form.confirmation.title') || 'تأكيد البيانات'}
            </h3>
            <p className="text-gray-500 text-lg max-w-md mx-auto">
                {t('property_form.confirmation.subtitle') || 'هل أنت متأكد من إضافة هذه البيانات؟ يرجى مراجعة كافة التفاصيل قبل الحفظ النهائي.'}
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {/* Media Preview */}
                <div className="bg-gray-50 rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="aspect-video relative bg-gray-200">
                        {mainPreview ? (
                            <img src={mainPreview} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                            <div className="flex items-center justify-center h-full text-gray-400">
                                No Main Image Selected
                            </div>
                        )}
                        <div className="absolute bottom-2 right-2 bg-black/60 text-white px-3 py-1 rounded-full text-xs backdrop-blur-sm">
                            {galleryCount} {t('property_form.gallery_images') || 'صور أخرى'}
                        </div>
                    </div>
                </div>

                {/* Details Summary */}
                <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 text-right space-y-3">
                    <h4 className="font-bold text-gray-700 border-b pb-2 mb-4">ملخص العقار:</h4>
                    <div className="flex justify-between gap-4">
                        <span className="text-gray-500 shrink-0">العنوان:</span>
                        <span className="font-medium truncate">{formData.ar.title}</span>
                    </div>
                    <div className="flex justify-between gap-4">
                        <span className="text-gray-500 shrink-0">العنوان الجغرافي:</span>
                        <span className="font-medium text-xs truncate max-w-[150px]">{formData.ar.address}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">السعر اليومي:</span>
                        <span className="font-medium text-primary">${formData.price_daily}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">الغرف:</span>
                        <span className="font-medium">{formData.rooms}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">الحمامات:</span>
                        <span className="font-medium">{formData.bathrooms}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">المساحة:</span>
                        <span className="font-medium underline decoration-primary/30 decoration-2">{formData.area} م²</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationStep;
